/**
 * Analysis Dialog Component
 *
 * Shows real-time progress of PRS analysis via WebSocket
 * and displays the results when complete.
 */

import React, {useEffect, useState, useCallback} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import {
  createWebSocket,
  getJobStatus,
  ProgressEvent,
} from '../services/agent';

interface AnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
  trialId: string;
  version: string;
  version2?: string;  // For comparison
}

export const AnalysisDialog: React.FC<AnalysisDialogProps> = ({
  open,
  onClose,
  jobId,
  trialId,
  version,
  version2,
}) => {
  const [status, setStatus] = useState<string>('pending');
  const [progressMessages, setProgressMessages] = useState<ProgressEvent[]>([]);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Handle WebSocket message
  const handleMessage = useCallback((event: ProgressEvent) => {
    setProgressMessages(prev => [...prev, event]);
    setStatus(event.status);

    // If we receive result data, store it
    if (event.data && event.status === 'completed') {
      setResult(event.data);
    }
  }, []);

  // Handle WebSocket error
  const handleError = useCallback(() => {
    setError('Connection lost. Fetching latest status...');

    // Poll for status if WebSocket fails
    if (jobId) {
      getJobStatus(jobId).then(job => {
        setStatus(job.status);
        if (job.result) {
          setResult(job.result);
        }
        if (job.error) {
          setError(job.error);
        }
      });
    }
  }, [jobId]);

  // Connect WebSocket when dialog opens with a jobId
  useEffect(() => {
    if (open && jobId) {
      const websocket = createWebSocket(
        jobId,
        handleMessage,
        handleError,
        () => console.log('WebSocket closed')
      );

      setWs(websocket);

      return () => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.close();
        }
      };
    }
  }, [open, jobId, handleMessage, handleError]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [ws]);

  const handleClose = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
    setProgressMessages([]);
    setResult(null);
    setError(null);
    setStatus('pending');
    onClose();
  };

  const getStatusIcon = (msgStatus: string) => {
    switch (msgStatus) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'running':
        return <PendingIcon color="primary" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const isComplete = status === 'completed';
  const isFailed = status === 'failed';
  const isRunning = status === 'running' || status === 'pending';

  return (
    <Dialog
      open={open}
      onClose={isRunning ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isRunning}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {version2
              ? `PRS Comparison: ${trialId} v${version} vs v${version2}`
              : `PRS Analysis: ${trialId} v${version}`
            }
          </Typography>
          <Chip
            label={status.toUpperCase()}
            color={isComplete ? 'success' : isFailed ? 'error' : 'primary'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Progress indicator */}
        {isRunning && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{mt: 1}}>
              Running AI analysis with Claude Sonnet 4.5...
            </Typography>
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {/* Progress messages */}
        {progressMessages.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Progress:
            </Typography>
            <List dense>
              {progressMessages.map((msg, index) => (
                <ListItem key={index}>
                  <Box mr={1} display="flex" alignItems="center">
                    {getStatusIcon(msg.status)}
                  </Box>
                  <ListItemText
                    primary={msg.message}
                    secondary={
                      msg.timestamp
                        ? new Date(msg.timestamp).toLocaleTimeString()
                        : ''
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Results */}
        {isComplete && result && (
          <Paper variant="outlined" sx={{p: 2, bgcolor: 'success.50'}}>
            <Typography variant="subtitle2" gutterBottom>
              {result.comparison ? 'Comparison Results:' : 'Analysis Results:'}
            </Typography>

            {result.cached && (
              <Chip
                label="From Cache"
                size="small"
                color="info"
                sx={{mb: 2}}
              />
            )}

            {/* Show comparison results */}
            {result.comparison && (
              <Box>
                <Typography variant="body2" component="pre" sx={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>
                  {typeof result.comparison === 'string'
                    ? result.comparison
                    : result.comparison.comparison_text || JSON.stringify(result.comparison, null, 2)}
                </Typography>
              </Box>
            )}

            {/* Show analysis results */}
            {result.analysis && !result.comparison && (
              <Box>
                {result.analysis.protocol_number && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Protocol:</strong> {result.analysis.protocol_number}
                  </Typography>
                )}

                {result.analysis.visits && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Visits:</strong> {result.analysis.visits.length}
                  </Typography>
                )}

                {result.analysis.questionnaires && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Questionnaires:</strong> {result.analysis.questionnaires.length}
                  </Typography>
                )}

                {result.analysis.summary && (
                  <Typography variant="body2" sx={{mt: 2}}>
                    <strong>Summary:</strong><br />
                    {result.analysis.summary}
                  </Typography>
                )}

                {/* Show raw analysis if available */}
                {result.analysis.analysis_text && (
                  <Typography variant="body2" component="pre" sx={{mt: 2, whiteSpace: 'pre-wrap'}}>
                    {result.analysis.analysis_text}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        {isComplete || isFailed ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <Button onClick={handleClose} disabled>
            Running...
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
